import { useCallback, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { To, useParams, useSearchParams } from 'react-router-dom'

import { Button, ButtonGroup, Divider, Intent } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import profile from '../../domain/api/profile'
import vcard from '../../domain/api/vcard'
import ApiError from '../common/apiError/ApiError'
import Mount from '../common/mount/Mount'
import ProfileDetail from '../profileDetail/ProfileDetail'
import { useRootOutletContext } from '../root/Root'
import styles from './Profile.module.scss'
import ProfileQrButton from './ProfileQrButton'
import ProfileSkeleton from './ProfileSkeleton'

export default ({
    navigatePostDeletingProfile,
}: {
    navigatePostDeletingProfile: (profileId: string) => To,
}) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const { profileId } = useParams()
    const { t } = useTranslation()
    const { toaster } = useRootOutletContext()
    const profileResponse = profile.useFetchSWR(profileId ?? '')
    const vcardUrl = vcard.url(profileId ?? '')
    const downloadDelaySeconds = 1

    const downloadVcard = useCallback(async () => {
        window.open(vcardUrl, '_self', 'noopener noreferrer')

        toaster?.show({
            message: t('toast.vcardDownloaded.message'),
            intent: Intent.SUCCESS,
            icon: IconNames.Download,
        })
    }, [toaster, t, vcardUrl])

    // consume 'download' query param and download vcard
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchParams.has('download'))
                setSearchParams(params => {
                    params.delete('download')
                    return params
                })
        }, (downloadDelaySeconds + 1) * 1000)
        return () => clearTimeout(timer)
    }, [searchParams, setSearchParams])

    return <>
        {searchParams.has('download') &&
            <Helmet>
                <meta httpEquiv="refresh" content={`${downloadDelaySeconds};${vcardUrl}`} />
            </Helmet>}

        <Mount response={profileResponse}
            loading={() => <ProfileSkeleton />}
            failure={error => <ApiError error={error} />}
            success={data => <>
                <ButtonGroup fill>
                    <Button
                        text={t('profile.downloadButton.label')}
                        intent={Intent.PRIMARY}
                        onClick={downloadVcard} />
                    <ProfileQrButton profileId={data.id} />
                </ButtonGroup>
                <Divider className={styles.spacer} />
                <ProfileDetail
                    data={data}
                    navigatePostDeletingProfile={navigatePostDeletingProfile}
                    canEdit />
            </>}
        />
    </>
}